import { 
  ChartBarIcon, 
  CurrencyDollarIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  TrophyIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  CubeIcon,
  ShoppingCartIcon,
  TruckIcon,
  BeakerIcon,
  ChartPieIcon,
  PresentationChartLineIcon,
  DocumentChartBarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

export interface Subsection {
  id: string;
  title: string;
  description: string;
  icon: any;
  sectionId: string;
  endpoint?: string;
}

export interface MainSection {
  id: string;
  title: string;
  color: string;
  gradient: string;
  subsections: Subsection[];
}

export const dashboardSections: MainSection[] = [
  {
    id: 'activity',
    title: 'Activity & Performance',
    color: '#2563EB',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    subsections: [
      {
        id: 'real-time-activity',
        title: 'Real-time Activity',
        description: 'Live feed of ongoing sales activities',
        icon: ChartBarIcon,
        sectionId: 'real-time-activity-section',
        endpoint: '/api/admin/activity/realtime'
      },
      {
        id: 'revenue-trends',
        title: 'Revenue Trends',
        description: 'Revenue analysis and forecasting',
        icon: CurrencyDollarIcon,
        sectionId: 'revenue-trends-section',
        endpoint: '/api/admin/activity/revenue'
      },
      {
        id: 'team-performance',
        title: 'Team Performance',
        description: 'Sales team metrics and rankings',
        icon: UserGroupIcon,
        sectionId: 'team-performance-section',
        endpoint: '/api/admin/activity/team'
      },
      {
        id: 'activity-summary',
        title: 'Activity Summary',
        description: 'Comprehensive activity overview',
        icon: ClipboardDocumentListIcon,
        sectionId: 'activity-summary-section',
        endpoint: '/api/admin/activity/summary'
      }
    ]
  },
  {
    id: 'team',
    title: 'Team & Clients',
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
    subsections: [
      {
        id: 'top-performers',
        title: 'Top Performers',
        description: 'Leaderboard and achievements',
        icon: TrophyIcon,
        sectionId: 'top-performers-section',
        endpoint: '/api/admin/team/performers'
      },
      {
        id: 'client-tracking',
        title: 'Client Tracking',
        description: 'Client engagement and status',
        icon: BuildingOfficeIcon,
        sectionId: 'client-tracking-section',
        endpoint: '/api/admin/team/clients'
      },
      {
        id: 'salesman-comparison',
        title: 'Salesman Comparison',
        description: 'Comparative performance analysis',
        icon: UsersIcon,
        sectionId: 'salesman-comparison-section',
        endpoint: '/api/admin/team/comparison'
      },
      {
        id: 'client-acquisition',
        title: 'Client Acquisition',
        description: 'New client growth metrics',
        icon: ArrowTrendingUpIcon,
        sectionId: 'client-acquisition-section',
        endpoint: '/api/admin/team/acquisition'
      }
    ]
  },
  {
    id: 'inventory',
    title: 'Inventory & Products',
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
    subsections: [
      {
        id: 'stock-analytics',
        title: 'Stock Analytics',
        description: 'Inventory levels and alerts',
        icon: CubeIcon,
        sectionId: 'stock-analytics-section',
        endpoint: '/api/admin/inventory/stock'
      },
      {
        id: 'product-performance',
        title: 'Product Performance',
        description: 'Best sellers and trends',
        icon: ShoppingCartIcon,
        sectionId: 'product-performance-section',
        endpoint: '/api/admin/inventory/products'
      },
      {
        id: 'supplier-analytics',
        title: 'Supplier Analytics',
        description: 'Supplier performance metrics',
        icon: TruckIcon,
        sectionId: 'supplier-analytics-section',
        endpoint: '/api/admin/inventory/suppliers'
      },
      {
        id: 'samples-tracking',
        title: 'Samples Tracking',
        description: 'Sample distribution and status',
        icon: BeakerIcon,
        sectionId: 'samples-tracking-section',
        endpoint: '/api/admin/inventory/samples'
      }
    ]
  },
  {
    id: 'analytics',
    title: 'Analytics & Insights',
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
    subsections: [
      {
        id: 'advanced-charts',
        title: 'Advanced Charts',
        description: 'Detailed visual analytics',
        icon: ChartPieIcon,
        sectionId: 'advanced-charts-section',
        endpoint: '/api/admin/analytics/charts'
      },
      {
        id: 'predictive-analytics',
        title: 'Predictive Analytics',
        description: 'AI-powered forecasting',
        icon: PresentationChartLineIcon,
        sectionId: 'predictive-analytics-section',
        endpoint: '/api/admin/analytics/predictive'
      },
      {
        id: 'custom-reports',
        title: 'Custom Reports',
        description: 'Build and save custom reports',
        icon: DocumentChartBarIcon,
        sectionId: 'custom-reports-section',
        endpoint: '/api/admin/analytics/reports'
      },
      {
        id: 'data-export',
        title: 'Data Export',
        description: 'Export data in various formats',
        icon: ArrowDownTrayIcon,
        sectionId: 'data-export-section',
        endpoint: '/api/admin/analytics/export'
      }
    ]
  }
];

export const getAllSubsections = (): Subsection[] => {
  return dashboardSections.flatMap(section => section.subsections);
};

export const getSubsectionById = (id: string): Subsection | undefined => {
  return getAllSubsections().find(sub => sub.id === id);
};

export const getSectionBySubsectionId = (subsectionId: string): MainSection | undefined => {
  return dashboardSections.find(section => 
    section.subsections.some(sub => sub.id === subsectionId)
  );
};

